import { Controller } from "@hotwired/stimulus"

/**
 * Kiosk Flash Controller
 *
 * Owns the auto-dismiss timer + shrinking-progress animation for the
 * persistent "{Neighbor} checked in" banner rendered into
 * `#kiosk_flash_slot` by the check_in turbo_stream response on every
 * successful (non-duplicate) check-in.
 *
 * Behavior:
 *   - On `connect`, kick off a requestAnimationFrame loop that scales the
 *     `progress` target's transform from 1 → 0 over `dismissMs`, then
 *     fades the element out and removes it from the DOM.
 *   - The `dismiss` action collapses the banner immediately (used by the
 *     Undo button's click handler so staff see the banner disappear
 *     before the DELETE request lands).
 *   - On `disconnect`, cancel any pending rAF so a Turbo Stream that
 *     swaps the slot mid-animation doesn't leave a leaked frame
 *     callback firing on a detached element.
 *
 * The undo-window countdown matches the dismiss timer (5s by default) so
 * staff see exactly how long they have. When the dismiss fires, the
 * Undo button is removed with the rest of the banner — by design,
 * because once the banner is gone there's no longer an attendance id in
 * the local scope to safely DELETE; staff fall back to the instance
 * detail page for older undos. Matches the spec's "undone window" UX.
 */
export default class extends Controller<HTMLElement> {
  static values = {
    dismissMs: { type: Number, default: 5000 },
    undoWindowMs: { type: Number, default: 5000 },
  }

  static targets = ["progress", "button"]

  declare readonly dismissMsValue: number
  declare readonly undoWindowMsValue: number
  declare readonly hasProgressTarget: boolean
  declare readonly progressTarget: HTMLElement
  declare readonly hasButtonTarget: boolean
  declare readonly buttonTarget: HTMLButtonElement

  private startedAt = 0
  private raf: number | null = null
  private dismissed = false

  connect() {
    this.startedAt = performance.now()
    this.tick()
  }

  disconnect() {
    this.stopTimer()
  }

  private stopTimer() {
    if (this.raf !== null) {
      cancelAnimationFrame(this.raf)
      this.raf = null
    }
  }

  // Action: bound to the Undo button via `click->kiosk-flash#dismiss`.
  //
  // This runs *during* the click that submits the `button_to` form, so it must
  // not touch anything the submission depends on. Disabling the button here — as
  // this used to — cancels the submit outright: form submission is the button's
  // activation behavior, and a button disabled during event dispatch never
  // performs it. The banner vanished and the DELETE was never sent.
  //
  // So the undo path only fades. The element stays in the DOM and enabled; the
  // response's `turbo_stream.update 'kiosk_flash_slot', ''` is what removes it,
  // which also means the banner cannot disappear before the undo actually lands.
  dismiss() {
    if (this.dismissed) return
    this.dismissed = true
    this.stopTimer()
    this.fadeOut()
    // Guards a double-tap without `disabled`, which would break the submit.
    this.element.classList.add("pointer-events-none")
  }

  private tick = () => {
    if (this.dismissed) return
    const elapsed = performance.now() - this.startedAt
    const remaining = Math.max(0, this.dismissMsValue - elapsed)
    const pct = remaining / this.dismissMsValue

    if (this.hasProgressTarget) {
      this.progressTarget.style.transform = `scaleX(${pct})`
    }

    if (remaining <= 0) {
      this.dismissed = true
      this.expire()
      return
    }

    this.raf = requestAnimationFrame(this.tick)
  }

  // The undo window ran out on its own. Nothing is in flight, so the banner can
  // take itself out — and the Undo button goes with it, which is the point: past
  // this moment there is no attendance id in scope to safely DELETE, and older
  // undos belong on the instance page.
  private expire() {
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = true
      this.buttonTarget.classList.add("opacity-40", "cursor-not-allowed")
    }
    this.fadeOut()
    // Remove after the transition so the slot is genuinely empty for the next
    // check-in's `turbo_stream.update` (which targets the *slot*, not this
    // banner — but a leftover banner would stack visually).
    window.setTimeout(() => {
      if (this.element.isConnected) {
        this.element.remove()
      }
    }, 220)
  }

  private fadeOut() {
    this.element.classList.add(
      "opacity-0",
      "translate-y-2",
      "transition-all",
      "duration-200",
    )
  }
}
